import { defineStore } from 'pinia';
import { ref } from 'vue';

import { documentsApiConfig } from '@/config/api/documents';
import { useClientApi } from '@/plugins/api/lk';
import {
  decorateApiActions,
  notifyDecorate,
} from '@/shared/lib';

interface GetDocumentsPayload {
  slug: string;
  bookingId: number;
}

interface DeleteDocumentPayload {
  ids: Array<string>;
  bookingId: number;
  documentId: number;
}

interface UploadDocumentsPayload {
  id: number;
  bookingId: number;
  payload: FormData;
}

interface Attachment {
  id: string;
  name: string;
}

interface Field {
  id: number;
  label: string;
  required: boolean;
  attachments: Array<Attachment>;
}

interface Document {
  title: string;
  description: string;
  label: string;
  hint: Record<string, string>;
  fields: Array<Field>;
}

export const useDocumentsStore = defineStore('documents', () => {
  const documents = ref<Array<Document>>([]);

  const setDocumentsList = (payload: Array<Document>) => {
    documents.value = payload;
  };

  const getDocuments = async ({ slug, bookingId }: GetDocumentsPayload) => {
    const url = documentsApiConfig.getQuizDocuments(slug, bookingId);
    const { data } = await useClientApi().get(url);
    setDocumentsList(data);
  };

  const sendPackageDocuments = notifyDecorate(async (bookingId: number) => {
    const url = documentsApiConfig.sendPackageDocuments(bookingId);
    await useClientApi().$post(url);
  }, 'sendPackageDocuments');

  const uploadDocuments = notifyDecorate(async ({
    bookingId,
    id,
    payload,
  }: UploadDocumentsPayload) => {
    const url = documentsApiConfig.uploadDocuments();
    const { data } = await useClientApi().post(url, payload, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: {
        bookingId,
        documentId: id,
      },
    });

    return data;
  }, 'uploadDocuments', true);

  const deleteDocument = async (payload: DeleteDocumentPayload) => {
    const {
      ids,
      bookingId,
      documentId,
    } = payload;
    const url = documentsApiConfig.deleteDocument();
    await useClientApi().patch(url, {
      file_ids: ids,
      booking_id: bookingId,
      document_id: documentId,
    });
  };

  const decoratedApiActions = decorateApiActions({
    getDocuments,
    deleteDocument,
  }, 'store/deal/documents', true);

  return {
    documents,
    sendPackageDocuments,
    uploadDocuments,
    ...decoratedApiActions,
  };
});
